import { useState, useEffect, useContext } from "react";
import PageTitle from "../PageTitle";
import AddIcon from "@mui/icons-material/Add";
import CreateProjectModal from "./CreateProjectModal";
import {
  Box,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import EnhancedTable from "../../layouts/table";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterListIcon from "@mui/icons-material/FilterList";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";

import FilterForm from "./FilterForm";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import LoadingTableRows from "../LoadingTableRows";
import toast from "react-hot-toast";
import { AppContext } from "../../scripts/context/App";
import ConfirmationModal from "../ConfirmationModal";

interface IFilter {
  status: boolean;
  data: {
    name: string;
    uuid: string;
    status: string;
  };
}

const initFilterData: IFilter = {
  status: true,
  data: {
    name: "",
    uuid: "",
    status: "",
  },
};

const initProjectState = {
  status: false,
  data: {
    name: "",
  },
};

export default function SmartContractManagement() {
  const { user } = useContext(AppContext);
  let navigate = useNavigate();
  const [tableData, setTableDatas] = useState({
    page: 1,
    pagesCount: -1,
    rowsPerPage: 5,
    rowsPerPageOptions: [5, 10, 25, 50, 100],
    tableHead: [
      {
        id: "name",
        numeric: false,
        disablePadding: false,
        label: "Name",
      },
      {
        id: "deployments_count",
        numeric: false,
        disablePadding: false,
        label: "Deployed Count",
      },
      {
        id: "uuid",
        numeric: false,
        disablePadding: false,
        label: "UUID",
      },
      {
        id: "description",
        numeric: false,
        disablePadding: false,
        label: "Description",
      },
      {
        id: "status",
        numeric: false,
        disablePadding: false,
        label: "Compile Status",
      },
      {
        id: "actions",
        numeric: false,
        disablePadding: false,
        label: "Actions",
      },
    ],
    tableItems: [],
  });
  const setTableData = (data: any) => setTableDatas({ ...tableData, ...data });
  const [project, setProject] = useState(initProjectState);
  const [filter, setFilter] = useState<IFilter>(initFilterData);
  const [order, setOrder] = useState({ order: "asc", orderBy: "name" });
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState({
    status: false,
    id: "",
    loading: false,
  });

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setTableData({
      rowsPerPage: Number(event.target.value),
      page: 1,
    });

    getProjects({
      ...filter.data,
      sortBy: order.orderBy,
      sortOrder: order.order,
      page: 1,
      perPage: event.target.value,
    });
  };
  const handleChangePage = (_: unknown, newPage: number) => {
    getProjects({
      ...filter.data,
      sortBy: order.orderBy,
      sortOrder: order.order,
      page: newPage,
      perPage: tableData.rowsPerPage,
    });

    setTableData({ page: newPage });
  };
  const getProjects = async (params?: any) => {
    const {
      data: { data },
    } = await axios.get("/smart-contract/get-projects", { params });

    setTableData({
      tableItems: data.data,
      pagesCount: data.total,
      rowsPerPage: data.per_page,
      page: data.current_page,
    });
  };
  const getInitData = async () => {
    if (!user.permissions.includes("view_project")) return;

    await getProjects();
    setLoading(false);
  };
  const deleteProject = async (uuid: string) => {
    if (!user.permissions.includes("delete_project")) return toast.error("You dont have permissions to delete projects");

    setConfirmDelete({ ...confirmDelete, loading: true });
    try {
      await axios.delete(
        "/smart-contract/delete-project",
        {
          params: { uuid },
        }
      );

      setTableData({
        tableItems: tableData.tableItems.filter(
          (item: any) => item.uuid !== uuid
        ),
      });
      toast.success("Project deleted");
      setConfirmDelete({ loading: false, id: "", status: false });
    } catch (error) {
      toast.error("Error deleting project");
      console.log({ error });

      setConfirmDelete({ loading: false, id: "", status: false });
    }
  }
  useEffect(() => {
    getInitData();
  }, []);

  if (!user.permissions.includes("view_project")) return (
    <Box sx={{ p: 3, textAlign: "center" }}>
      <h3>You dont have permissions to view this page</h3>
    </Box>
  )

  return (
    <>
      <PageTitle
        title="Smart Contract Management"
        description="Manage smart contracts"
        actionLabel="Create new project"
        action={() => {
          if (!user.permissions.includes("create_project")) return toast.error("You dont have permissions to create new project");

          setProject({
            ...project,
            status: true,
          });
        }}
        startIcon={<AddIcon />}
      />

      <CreateProjectModal
        data={project}
        setData={(object: any) =>
          setProject({
            ...project,
            data: { ...project.data, ...object },
          })
        }
        addNewProject={(newProject) => {
          setTableData({ tableItems: [newProject, ...tableData.tableItems] });
        }}
        handleClose={() => {
          setProject(initProjectState);
        }}
      />

      <ConfirmationModal
        open={confirmDelete.status}
        onClose={() => setConfirmDelete({ ...confirmDelete, status: false })}
        onConfirm={() => deleteProject(confirmDelete.id)}
        title="Are you sure?"
        message={"This action cannot be undone, please confirm to continue"}
        loading={confirmDelete.loading}
      />

      <Box sx={{ p: 3 }}>
        <EnhancedTable
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          handleChangePage={handleChangePage}
          minTableHeadWidth="200px"
          handleRequestSortHeader={(orderFunc, orderByFunc) => {
            if (!orderByFunc) return;

            getProjects({
              name: filter.data.name,
              uuid: filter.data.uuid,
              status: filter.data.status,
              sortBy: orderByFunc,
              sortOrder: orderFunc,
              page: 1,
              perPage: tableData.rowsPerPage,
            });
            setOrder({
              order: orderByFunc,
              orderBy: orderFunc,
            });
          }}
          TableRows={
            loading ? (
              <LoadingTableRows elementsCount={tableData.tableHead.length} />
            ) : tableData.tableItems.length > 0 ? (
              tableData.tableItems.map((row: any, index: number) => (
                <TableRow key={index}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.deployments_count}</TableCell>
                  <TableCell>{row.uuid}</TableCell>
                  <TableCell>
                    {row.description || <HorizontalRuleIcon />}
                  </TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell>
                    <Tooltip title="Copy UUID">
                      <IconButton
                        color="primary"
                        onClick={() => {
                          navigator.clipboard.writeText(row.uuid);

                          toast.success("UUID copied to clipboard");
                        }}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        color="primary"
                        onClick={() => {
                          if (!user.permissions.includes("view_project_details")) return toast.error("You dont have permissions to view project details");

                          navigate(`edit/${row.uuid}`);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete project">
                      <IconButton
                        color="error"
                        onClick={() => setConfirmDelete({ ...confirmDelete, status: true, id: row.uuid })}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={tableData.tableHead.length}>
                  <Typography
                    variant="body2"
                    color="textPrimary"
                    sx={{ textAlign: "center" }}
                  >
                    No results found
                  </Typography>
                </TableCell>
              </TableRow>
            )
          }
          {...tableData}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                onClick={() =>
                  setFilter({
                    ...filter,
                    status: !filter.status,
                  })
                }
              >
                {filter.status ? <FilterListIcon /> : <FilterListOffIcon />}
              </IconButton>
              <Typography variant="h6">Filter by</Typography>
            </Box>
            {filter.status && (
              <FilterForm
                filter={filter}
                setFilter={setFilter}
                handleSearch={(data) => {
                  if (data?.data) {
                    getProjects({
                      ...data.data,
                      page: 1,
                      perPage: tableData.rowsPerPage,
                    });
                  } else {
                    getProjects({
                      ...filter.data,
                      page: 1,
                      perPage: tableData.rowsPerPage,
                    });
                  }
                }}
              />
            )}
          </Box>
        </EnhancedTable>
      </Box>
    </>
  );
}
